import * as React from 'react';
import { CheckCircle, AlertTriangle, XCircle, Info, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { renderFeedbackIcon } from '../shared/renderFeedbackIcon';

/**
 * Toast variant options based on semantic meaning
 * - success: Positive feedback (action completed, saved)
 * - warning: Caution states (pending action, review needed)
 * - error: Negative feedback (validation errors, failures)
 * - info: Neutral information (tips, notifications)
 */
export type ToastVariant = 'success' | 'warning' | 'error' | 'info';

/**
 * Props for an individual toast notification
 */
export interface ToastData {
  /**
   * Unique identifier for the toast
   */
  id: string;
  /**
   * The visual variant of the toast
   * @default 'info'
   */
  variant?: ToastVariant;
  /**
   * Optional title displayed prominently
   */
  title?: string;
  /**
   * The message content
   */
  message?: React.ReactNode;
  /**
   * Duration in milliseconds before auto-dismiss
   * Set to 0 to disable auto-dismiss
   * @default 5000
   */
  duration?: number;
  /**
   * Custom icon to display. If not provided, uses default icon per variant.
   * Set to null to hide the icon entirely.
   */
  icon?: React.ReactNode | null;
}

/**
 * Internal toast state with animation tracking
 */
interface ToastState extends ToastData {
  exiting?: boolean;
}

/**
 * Context value for toast management
 */
interface ToastContextValue {
  /**
   * Show a new toast notification
   */
  toast: (options: Omit<ToastData, 'id'>) => string;
  /**
   * Dismiss a specific toast by ID
   */
  dismiss: (id: string) => void;
  /**
   * Dismiss all toasts
   */
  dismissAll: () => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

/**
 * Hook to access toast functions
 * @throws Error if used outside ToastProvider
 */
export function useToast(): ToastContextValue {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

/**
 * Default icons for each variant
 */
const defaultIcons: Record<ToastVariant, LucideIcon> = {
  success: CheckCircle,
  warning: AlertTriangle,
  error: XCircle,
  info: Info,
};

/**
 * Variant styles based on the design system semantic colors.
 * Toast uses white bg (unlike Alert which uses subtle colored backgrounds).
 */
const variantStyles: Record<
  ToastVariant,
  { bg: string; text: string; icon: string; border: string }
> = {
  success: {
    bg: 'bg-secondary-100',
    text: 'text-success-800',
    icon: 'text-success-700',
    border: 'border-l-success-700',
  },
  warning: {
    bg: 'bg-secondary-100',
    text: 'text-warning-800',
    icon: 'text-warning-700',
    border: 'border-l-warning-700',
  },
  error: {
    bg: 'bg-secondary-100',
    text: 'text-error-800',
    icon: 'text-error-700',
    border: 'border-l-error-700',
  },
  info: {
    bg: 'bg-secondary-100',
    text: 'text-info-800',
    icon: 'text-info-700',
    border: 'border-l-info-700',
  },
};

/**
 * Individual toast component
 */
interface ToastItemProps {
  toast: ToastState;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: Readonly<ToastItemProps>) {
  const { id, variant = 'info', title, message, icon, exiting } = toast;
  const styles = variantStyles[variant];
  const DefaultIcon = defaultIcons[variant];

  const animationClass = exiting
    ? 'animate-toast-exit'
    : 'animate-toast-enter';

  return (
    <output
      aria-live="polite"
      className={[
        'relative flex gap-3',
        'rounded-lg',
        'p-4',
        'border-l-4',
        styles.border,
        styles.bg,
        'shadow-lg',
        'max-w-[360px] w-full',
        animationClass,
        // Support for reduced motion
        'motion-reduce:animate-none motion-reduce:opacity-100 motion-reduce:translate-x-0',
      ].join(' ')}
    >
      {renderFeedbackIcon(icon, DefaultIcon, styles.icon)}

      <div className="flex-1 min-w-0">
        {title && (
          <h3 className={`text-sm font-semibold ${styles.text} ${message ? 'mb-1' : ''}`}>
            {title}
          </h3>
        )}
        {message && (
          <div className="text-sm text-secondary-600">{message}</div>
        )}
      </div>

      <button
        type="button"
        onClick={() => onDismiss(id)}
        className="flex-shrink-0 p-0.5 rounded hover:bg-black/5 transition-colors duration-150 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-200"
        aria-label="Dismiss notification"
      >
        <X
          className="w-5 h-5 text-secondary-400 hover:text-secondary-600"
          aria-hidden="true"
        />
      </button>
    </output>
  );
}

/**
 * Props for ToastProvider
 */
export interface ToastProviderProps {
  /**
   * Application content
   */
  children: React.ReactNode;
  /**
   * Maximum number of visible toasts at once
   * @default 3
   */
  maxVisible?: number;
  /**
   * Default duration for auto-dismiss in milliseconds
   * @default 5000
   */
  defaultDuration?: number;
}

/**
 * Generate a unique ID for toasts
 */
let toastCounter = 0;
function generateId(): string {
  return `toast-${Date.now()}-${++toastCounter}`;
}

/**
 * ToastProvider wraps the application and provides toast functionality.
 *
 * Toasts are displayed in the bottom-right corner of the viewport,
 * stacking with newer toasts on top.
 *
 * @example
 * // Wrap your app with ToastProvider
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 *
 * @example
 * // Use the useToast hook in components
 * function MyComponent() {
 *   const { toast } = useToast();
 *
 *   return (
 *     <button onClick={() => toast({
 *       variant: 'success',
 *       title: 'Saved!',
 *       message: 'Your changes have been saved.'
 *     })}>
 *       Save
 *     </button>
 *   );
 * }
 */
export function ToastProvider({
  children,
  maxVisible = 3,
  defaultDuration = 5000
}: Readonly<ToastProviderProps>) {
  const [toasts, setToasts] = React.useState<ToastState[]>([]);
  const timersRef = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // Cleanup timers on unmount
  React.useEffect(() => {
    return () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  // Helper to remove a toast from state (used after exit animation)
  const removeToast = React.useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const dismiss = React.useCallback((id: string) => {
    // Clear any existing timer
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }

    // Start exit animation
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, exiting: true } : t))
    );

    // Remove after exit animation (150ms)
    setTimeout(() => removeToast(id), 150);
  }, [removeToast]);

  const dismissAll = React.useCallback(() => {
    toasts.forEach((t) => dismiss(t.id));
  }, [toasts, dismiss]);

  const toast = React.useCallback(
    (options: Omit<ToastData, 'id'>): string => {
      const id = generateId();
      const duration = options.duration ?? defaultDuration;

      const newToast: ToastState = {
        id,
        variant: 'info',
        ...options,
      };

      setToasts((prev) => {
        // Add new toast at the beginning (top of stack)
        const updated = [newToast, ...prev];
        // Remove oldest toasts if we exceed max
        return updated.slice(0, maxVisible);
      });

      // Set auto-dismiss timer if duration > 0
      if (duration > 0) {
        const timer = setTimeout(() => {
          dismiss(id);
        }, duration);
        timersRef.current.set(id, timer);
      }

      return id;
    },
    [defaultDuration, maxVisible, dismiss]
  );

  const contextValue = React.useMemo(
    () => ({ toast, dismiss, dismissAll }),
    [toast, dismiss, dismissAll]
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      {/* Toast container */}
      <div
        className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 pointer-events-none"
        aria-label="Notifications"
      >
        {toasts.map((t) => (
          <div key={t.id} className="pointer-events-auto">
            <ToastItem toast={t} onDismiss={dismiss} />
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/**
 * Toast component namespace for convenience
 */
export const Toast = {
  Provider: ToastProvider,
  useToast,
};
